import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getEnabledChannels } from '@/lib/queries/channels'
import { getAllProducts } from '@/lib/queries/products'
import { getAllBundles } from '@/lib/queries/bundles'
import { getAllStockItems } from '@/lib/queries/stockItems'
import { getActiveListings } from '@/lib/queries/listings'
import { getIncompleteWorkflows } from '@/lib/queries/workflows'

/**
 * Dashboard page - main landing page
 * Mobile-first design optimized for iPad Mini
 * Based on wireframe: dashboard.html
 */
export default function Dashboard() {
  useQuery({
    queryKey: ['channels', 'enabled'],
    queryFn: getEnabledChannels,
  })

  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: getAllProducts,
    staleTime: 0,
    gcTime: 0,
  })

  const { data: bundles, isLoading: bundlesLoading } = useQuery({
    queryKey: ['bundles'],
    queryFn: getAllBundles,
  })

  const { data: stockItems, isLoading: stockItemsLoading } = useQuery({
    queryKey: ['stockItems'],
    queryFn: getAllStockItems,
  })

  const { data: activeListings, isLoading: listingsLoading } = useQuery({
    queryKey: ['listings', 'active'],
    queryFn: getActiveListings,
    staleTime: 0,
    gcTime: 0,
  })

  const { data: incompleteWorkflows } = useQuery({
    queryKey: ['workflows', 'incomplete'],
    queryFn: getIncompleteWorkflows,
  })

  // Calculate stats
  const publishedBundles = bundles?.filter((b) => b.status === 'published').length || 0
  const draftBundles = bundles?.filter((b) => b.status === 'draft').length || 0
  const totalBundles = bundles?.length || 0

  // Calculate low stock items (bundles below reorder point)
  const lowStockBundles = bundles?.filter(
    (b) => b.currentStock !== undefined && b.reorderPoint !== undefined && b.currentStock <= b.reorderPoint
  ).length || 0

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 gap-3 mb-6">
          <Link
            to="/stock-items"
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow touch-target-min-44"
          >
            <h3 className="text-sm text-gray-600 mb-1">Stock Items</h3>
            <div className="text-3xl font-bold text-gray-900">
              {stockItemsLoading ? '...' : stockItems?.length || 0}
            </div>
            <div className="text-xs text-gray-500 mt-1">Total items</div>
          </Link>

          <Link
            to="/bundles"
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow touch-target-min-44"
          >
            <h3 className="text-sm text-gray-600 mb-1">Bundles</h3>
            <div className="text-3xl font-bold text-gray-900">
              {bundlesLoading ? '...' : totalBundles}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {publishedBundles} published, {draftBundles} drafts
            </div>
          </Link>

          <Link
            to="/products"
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow touch-target-min-44"
          >
            <h3 className="text-sm text-gray-600 mb-1">Products</h3>
            <div className="text-3xl font-bold text-gray-900">
              {productsLoading ? '...' : products?.length || 0}
            </div>
            <div className="text-xs text-gray-500 mt-1">Across all channels</div>
          </Link>

          <Link
            to="/listings"
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow touch-target-min-44"
          >
            <h3 className="text-sm text-gray-600 mb-1">Active Listings</h3>
            <div className="text-3xl font-bold text-gray-900">
              {listingsLoading ? '...' : activeListings?.length || 0}
            </div>
            <div className="text-xs text-gray-500 mt-1">Live on channels</div>
          </Link>

          {lowStockBundles > 0 && (
            <Link
              to="/bundles?filter=low-stock"
              className="bg-white border-2 border-red-500 rounded-lg p-4 hover:shadow-md transition-shadow touch-target-min-44"
            >
              <h3 className="text-sm text-red-600 mb-1 font-semibold">Low Stock Alert</h3>
              <div className="text-3xl font-bold text-red-600">{lowStockBundles}</div>
              <div className="text-xs text-gray-500 mt-1">Items need attention</div>
            </Link>
          )}
        </div>

        {/* Quick Actions */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/listings/new"
              className="block w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-medium touch-target-min-44 text-center"
            >
              Create a Listing
            </Link>
            <Link
              to="/workflows/product"
              className="block w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-medium touch-target-min-44 text-center"
            >
              Create a Product
            </Link>
            <Link
              to="/zoho-inventory"
              className="block w-full bg-white text-blue-600 border-2 border-blue-600 py-4 px-6 rounded-lg font-medium touch-target-min-44 text-center"
            >
              View Inventory
            </Link>
            <Link
              to="/import"
              className="block w-full bg-white text-blue-600 border-2 border-blue-600 py-4 px-6 rounded-lg font-medium touch-target-min-44 text-center hover:bg-blue-50 transition-colors"
            >
              Import from Channel
            </Link>
            <button
              type="button"
              className="block w-full bg-white text-blue-600 border-2 border-blue-300 py-4 px-6 rounded-lg font-medium touch-target-min-44 text-center opacity-50 cursor-not-allowed"
              disabled
            >
              Create a Bundle (coming soon)
            </button>
            <button
              type="button"
              className="block w-full bg-white text-blue-600 border-2 border-blue-300 py-4 px-6 rounded-lg font-medium touch-target-min-44 text-center opacity-50 cursor-not-allowed"
              disabled
            >
              Add Stock Items (coming soon)
            </button>
          </div>
        </section>

        {/* Incomplete Workflows */}
        {incompleteWorkflows && incompleteWorkflows.length > 0 && (
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Incomplete Workflows</h2>
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Resume where you left off</h3>
              <div className="space-y-2">
                {incompleteWorkflows.map((workflow) => (
                  <div
                    key={workflow._id}
                    className="bg-white rounded p-3 flex items-center justify-between"
                  >
                    <div>
                      <strong className="text-sm text-gray-900">
                        {workflow.data?.workflowType || 'Workflow'}
                      </strong>
                      <div className="text-xs text-gray-600 mt-1">
                        Step {workflow.currentStep + 1} of {workflow.data?.totalSteps || '?'}
                      </div>
                    </div>
                    <Link
                      to={`/workflows/resume/${workflow.workflowId}`}
                      className="text-blue-600 text-sm font-medium"
                    >
                      Continue ?
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Floating Action Button (FAB) */}
      <Link
        to="/listings/new"
        className="fixed bottom-24 right-4 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center text-2xl font-bold z-50 touch-target-min-44 hover:bg-blue-700 transition-colors"
        aria-label="Create a Listing"
      >
        +
      </Link>
    </div>
  )
}
